import React, { useState, useRef, useEffect } from 'react';

const VatBoxPopover = ({ title, accounts, period, totalLabel }) => {
    const [isOpen, setIsOpen] = useState(false);
    const popoverRef = useRef(null);
    const buttonRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                popoverRef.current && !popoverRef.current.contains(event.target) &&
                buttonRef.current && !buttonRef.current.contains(event.target)
            ) {
                setIsOpen(false);
            }
        };
        if (isOpen) document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    const formatCurrency = (val) =>
        new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(val || 0);

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        return new Date(dateStr).toLocaleDateString('sv-SE', { year: 'numeric', month: 'short', day: 'numeric' });
    };

    const total = accounts?.reduce((sum, acc) => sum + (acc.amount || 0), 0) || 0;

    return (
        <div style={{ position: 'relative', display: 'inline-block' }}>
            <button
                type="button"
                ref={buttonRef}
                onClick={() => setIsOpen(!isOpen)}
                style={{
                    background: 'transparent', border: 'none', cursor: 'pointer', padding: '2px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    opacity: isOpen ? 1 : 0.45, transition: 'opacity 0.15s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.opacity = 1}
                onMouseLeave={(e) => e.currentTarget.style.opacity = isOpen ? 1 : 0.45}
                title="Visa kontofördelning"
            >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ color: 'var(--secondary)' }}>
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                    <path d="M12 16V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    <circle cx="12" cy="8" r="1" fill="currentColor" />
                </svg>
            </button>

            {isOpen && (
                <div
                    ref={popoverRef}
                    style={{
                        position: 'absolute', top: '100%', right: 0,
                        marginTop: '8px', backgroundColor: 'white', borderRadius: '10px',
                        boxShadow: '0 10px 40px rgba(0,0,0,0.15)', border: '1px solid #e2e8f0',
                        padding: '14px', width: '360px', zIndex: 1000,
                        fontFamily: 'system-ui, -apple-system, sans-serif',
                        maxHeight: '420px', overflowY: 'auto'
                    }}
                >
                    {/* Title */}
                    <div style={{
                        fontSize: '0.9rem', fontWeight: 700, color: '#1e293b',
                        marginBottom: '10px', paddingBottom: '8px', borderBottom: '1px solid #e2e8f0'
                    }}>
                        {title}
                    </div>

                    {accounts && accounts.length > 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {accounts.map((account, i) => (
                                <div key={i} style={{
                                    backgroundColor: '#f8fafc', borderRadius: '8px',
                                    border: '1px solid #e2e8f0', padding: '8px 10px'
                                }}>
                                    {/* Account header */}
                                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: account.verifications?.length > 0 ? '6px' : 0 }}>
                                        <span style={{ fontSize: '0.78rem', fontWeight: 700, fontFamily: 'monospace', color: '#1e293b' }}>
                                            {account.accountNumber}
                                        </span>
                                        <span style={{ fontSize: '0.72rem', color: 'var(--secondary)', marginLeft: '6px' }}>
                                            {account.accountName}
                                        </span>
                                    </div>

                                    {/* Verifications under this account */}
                                    {account.verifications && account.verifications.length > 0 && (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1px', paddingLeft: '14px', borderLeft: '2px solid #cbd5e1', marginLeft: '10px' }}>
                                            {account.verifications.map((ver, j) => (
                                                <div key={j} style={{
                                                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                                    padding: '3px 6px', fontSize: '0.72rem', color: 'var(--secondary)'
                                                }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                        <span style={{ fontFamily: 'monospace', fontWeight: 600, color: '#475569' }}>
                                                            {ver.label}
                                                        </span>
                                                        {ver.date && (
                                                            <span style={{ color: '#94a3b8', fontSize: '0.68rem' }}>
                                                                {formatDate(ver.date)}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <span style={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                                                        {formatCurrency(ver.amount)}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}

                            {/* Total */}
                            <div style={{
                                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                paddingTop: '8px', borderTop: '1px solid #e2e8f0', marginTop: '2px'
                            }}>
                                <span style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--secondary)' }}>
                                    {totalLabel || 'Totalt'}
                                </span>
                                <span style={{ fontSize: '0.88rem', fontWeight: 700, color: '#1e293b' }}>
                                    {formatCurrency(total)}
                                </span>
                            </div>
                        </div>
                    ) : (
                        <div style={{ fontSize: '0.8rem', color: '#94a3b8', fontStyle: 'italic', padding: '8px 0' }}>
                            Laddar kontofördelning...
                        </div>
                    )}

                    {/* Period */}
                    {period?.startDate && period?.endDate && (
                        <div style={{
                            fontSize: '0.72rem', color: '#94a3b8', marginTop: '10px',
                            display: 'flex', alignItems: 'center', gap: '4px'
                        }}>
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" style={{ color: '#94a3b8' }}>
                                <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" />
                                <path d="M16 2V6M8 2V6M3 10H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                            </svg>
                            {formatDate(period.startDate)} – {formatDate(period.endDate)}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default VatBoxPopover;
